// https://kit.svelte.dev/docs/hooks#shared-hooks
import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import { dev } from '$app/environment';
import { PUBLIC_BUILD_NAME, PUBLIC_SENTRY_DSN } from '$env/static/public';

// polyfill for crypto.randomUUID
(() => {
    if (typeof crypto === 'undefined' || !crypto.randomUUID) {
        // window.crypto 객체가 없다면 정의
        if (typeof window !== 'undefined' && !window.crypto) {
            window.crypto = {};
        }

        const cryptoObj = typeof window !== 'undefined' ? window.crypto : crypto;

        // 새로운 UUID 생성 로직 적용
        cryptoObj.randomUUID = function () {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0,
                    v = c == 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        };
    }
})();

if (!dev && PUBLIC_SENTRY_DSN) {
    Sentry.init({
        dsn: PUBLIC_SENTRY_DSN,
        environment: PUBLIC_BUILD_NAME,
        // integrations: [new Sentry.Replay()],
        // Performance Monitoring
        tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
        // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        beforeSend(event, hint) {
            // 401 Unauthorized는 인증 만료로 인한 정상 흐름이므로 Sentry에 보내지 않음
            const error = hint?.originalException as any;
            if (error?.response?.status === 401 || error?.status === 401) {
                return null;
            }
            // 의도된 제어 흐름 신호가 단 새 error-level sentinel marker(세대 대체·명시 가드·사용자 취소) — 오류가 아니므로 보내지 않음
            if (error?.ignoreSentry === true) {
                return null;
            }

            // 요청 config의 ignoreSentry — 화면이 직접 안내한 BE 검증 거절처럼 요청 단위로 수집 제외가
            // 표시된 에러다. axios 인터셉터를 거치지 않는 전역(unhandled rejection) 경로에서도 같은 플래그를 적용한다.
            if ((error?.config as { ignoreSentry?: boolean } | undefined)?.ignoreSentry === true) {
                return null;
            }

            event.tags = {
                ...event.tags,
                build_date: __BUILD_DATE__,
                errorId: crypto.randomUUID(),
            };
            return event;
        },
    });
}

export async function handleError({ error, event }): Promise<HandleClientError> {
    const errorId = crypto.randomUUID();
    if (!dev) {
        Sentry.captureException(error, { extra: { event, errorId } });
    } else {
        console.warn('%c⚠️ handleError Event:', 'color: yellow; background: black; font-size: 16px', event);
        console.error('🔥', error);
    }

    const err = error as any;
    const status = err?.response?.status || err?.status;
    return {
        errorId,
        status,
        message: err?.message || 'An unexpected error occurred',
        ...err?.data,
    };
}
